// utils/quantityLinks.js
//
// Shared helpers that make Quantity Calculator "links" live instead of a one-time copy.
//
// A calculator row can be linked to another line item's quantity (row.linkedSourceId).
// Historically the source quantity was copied into row.length once, at link time. These
// helpers let every page (and the calculator modal) re-read the source's CURRENT quantity
// and recompute dependent totals, so changing a source propagates to its consumers.
//
// No backend/DB changes are required: linkedSourceId already persists inside the
// line_item_rows JSON, and each source line item carries its own current quantity.

import { evaluate, isUnit } from 'mathjs';

// ─── Row math (kept identical to QuantityCalculatorModal.recalcRow) ──────────

/**
 * What a calculator cell is worth: a number or a formula (leading "=" optional), as before. A result
 * WITH units ("250 mm", "0.2 L", "120 kg / (7850 kg/m^3)") is taken in SI base units — 250 mm → 0.25,
 * 0.2 L → 0.0002 — so sizes can be typed as they're read off a drawing. Unreadable → 0.
 */
export const evaluateCell = (raw) => {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return 0;
  try {
    const r = evaluate(s.startsWith('=') ? s.slice(1) : s);
    if (isUnit(r)) return Number.isFinite(r.value) ? r.value : 0;
    const n = typeof r === 'number' ? r : Number(r);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const evaluateFormula = (formula) => evaluateCell(formula);

const hasNumericValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = typeof value === 'number' ? value.toString() : value;
  if (typeof str !== 'string' || str.trim() === '') return false;
  const ev = evaluateFormula(str.startsWith('=') ? str.slice(1) : str);
  return !isNaN(ev) && ev !== 0;
};

/**
 * Compute a single calculator row's total: count × length × width × height × factor,
 * where empty/zero fields default to 1. Returns 0 when the row has no numeric input.
 */
export const recalcRowTotal = (row) => {
  const hasAny =
    hasNumericValue(row.count) ||
    hasNumericValue(row.length) ||
    hasNumericValue(row.width) ||
    hasNumericValue(row.height) ||
    hasNumericValue(row.factor);

  if (hasAny) {
    const count = parseFloat(row.count_evaluated || 0) || 1;
    const length = parseFloat(row.length_evaluated || 0) || 1;
    const width = parseFloat(row.width_evaluated || 0) || 1;
    const height = parseFloat(row.height_evaluated || 0) || 1;
    const factor = parseFloat(row.factor_evaluated || 0) || 1;
    return count * length * width * height * factor;
  }
  return 0;
};

// ─── Link helpers ───────────────────────────────────────────────────────────

/**
 * Normalise a free-text key component (trim, collapse internal whitespace, lowercase)
 * so description-based link keys round-trip regardless of incidental spacing/casing.
 */
export const normKey = (str) =>
  (str == null ? '' : String(str)).trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * The STABLE id used to identify a line item for linking. Must match how the calculator
 * stores row.linkedSourceId (QuantityCalculatorModal.handleQuantitySelect calls this).
 *
 * Precedence, most-stable first:
 *   - item.linkKey    → an explicit, reload-stable key a page attaches to each row
 *                       (e.g. a normalised description or DB id). Preferred because the
 *                       composite/breakdown pages otherwise only carry ephemeral uuids.
 *   - item.factorId   → the DB line item id (lowercase) that BuildNewCompositePage keeps
 *                       across reloads; stable, unlike the ephemeral uppercase FactorId.
 *   - item.lineItemId / item.FactorId / item.id → legacy fallbacks. Working pages
 *                       (MaterialEditorPage, CostEditorPage) set only FactorId (the DB id),
 *                       so they resolve exactly as before.
 *
 * Always coerced to a string for stable Map keys.
 */
export const linkKey = (item) => {
  const id =
    item?.linkKey ??
    item?.factorId ??
    item?.lineItemId ??
    item?.FactorId ??
    item?.id;
  return id == null ? null : String(id);
};

/** A line item's current quantity, tolerating both naming conventions across pages. */
export const itemQuantity = (item) => parseFloat(item?.quantity ?? item?.Factor) || 0;

/** Build a Map(linkKey -> current quantity) from a list of sibling line items. */
export const buildSourceQtyMap = (lineItems) => {
  const map = new Map();
  (lineItems || []).forEach((item) => {
    const key = linkKey(item);
    if (key != null) map.set(key, itemQuantity(item));
  });
  return map;
};

/**
 * Re-read linked rows against the current source quantities.
 *
 * For each row carrying a linkedSourceId that resolves in qtyMap (and is not the row's own
 * line item), overwrite length/length_evaluated with the source's current quantity and
 * recompute the row total. Rows whose source is missing (deleted/broken link) are left
 * untouched so the last known value is preserved. All other fields are preserved.
 *
 * @returns {{ rows: Array, changed: boolean }}
 */
export const resolveRows = (rows, qtyMap, selfId) => {
  let changed = false;
  const self = selfId == null ? null : String(selfId);

  const resolved = (rows || []).map((row) => {
    if (!row || row.linkedSourceId == null) return row;

    const sourceId = String(row.linkedSourceId);
    if (sourceId === self || !qtyMap.has(sourceId)) return row;

    const q = qtyMap.get(sourceId);
    if (q == null || isNaN(q)) return row;

    const next = { ...row, length: String(q), length_evaluated: q };
    next.total = recalcRowTotal(next);

    if (next.length !== row.length || next.total !== row.total) changed = true;
    return next;
  });

  return { rows: resolved, changed };
};

/** A line item's display description, tolerating both naming conventions across pages. */
export const linkDescription = (item) =>
  item?.description ?? item?.CostMeasurementName ?? item?.QuantityUnitName ?? item?.Name ?? '';

const LINKED_FROM_RE = /^linked from:\s*(.+)$/i;

/**
 * Auto-heal legacy broken links.
 *
 * Historically links were keyed on an ephemeral per-load uuid, so a linkedSourceId saved in
 * a prior session no longer matches any current sibling after reload. The row still carries a
 * `Linked from: {sourceDescription}` label, which we use to re-point the orphaned link at the
 * sibling whose description matches — restoring the live link without user action.
 *
 * Safety: only heals a link that does NOT currently resolve, and only when exactly ONE sibling
 * matches the label (0 or ambiguous matches are left untouched — the "last value is kept"
 * behaviour, so nothing regresses). The healed key is a stable one (linkKey), so the next Save
 * makes the repair permanent.
 *
 * @returns {{ rows: Array, changed: boolean }}
 */
export const healLegacyLinks = (rows, lineItems) => {
  const qtyMap = buildSourceQtyMap(lineItems);

  // Index sibling stable keys by normalised description (only descriptions that are unique
  // across the sibling set are usable — duplicates are dropped so we never guess).
  const byDesc = new Map();
  (lineItems || []).forEach((item) => {
    const key = linkKey(item);
    if (key == null) return;
    const dk = normKey(linkDescription(item));
    if (!dk) return;
    if (!byDesc.has(dk)) byDesc.set(dk, new Set());
    byDesc.get(dk).add(String(key));
  });

  let changed = false;
  const healed = (rows || []).map((row) => {
    if (!row || row.linkedSourceId == null) return row;
    if (qtyMap.has(String(row.linkedSourceId))) return row; // already resolves — leave it

    const m = LINKED_FROM_RE.exec((row.description || '').trim());
    if (!m) return row;

    const keys = byDesc.get(normKey(m[1]));
    if (!keys || keys.size !== 1) return row; // 0 or ambiguous → keep last value

    const newId = keys.values().next().value;
    if (newId === String(row.linkedSourceId)) return row;

    changed = true;
    return { ...row, linkedSourceId: newId };
  });

  return { rows: healed, changed };
};

/** Sum row totals, treating non-numeric totals as 0 (negative-aware). */
export const sumRowTotals = (rows) =>
  (rows || []).reduce((sum, row) => {
    const t = parseFloat(row && row.total);
    return sum + (isNaN(t) ? 0 : t);
  }, 0);

// ─── Dependency graph (for chained links + cycle prevention) ─────────────────

/**
 * Build a directed graph of link dependencies: Map(itemId -> Set of source ids it
 * links to). An edge X -> S means "X's quantity depends on S".
 *
 * @param {Array} lineItems  Sibling line items.
 * @param {object} rowsById  Map of itemId (linkKey) -> that item's calculator rows.
 */
export const buildLinkGraph = (lineItems, rowsById) => {
  const graph = new Map();
  (lineItems || []).forEach((item) => {
    const id = linkKey(item);
    if (id == null) return;
    const rows = (rowsById && rowsById[id]) || [];
    const sources = new Set();
    rows.forEach((r) => {
      if (r && r.linkedSourceId != null) sources.add(String(r.linkedSourceId));
    });
    graph.set(id, sources);
  });
  return graph;
};

/**
 * All ids that (directly or transitively) depend on targetId — i.e. nodes from which
 * targetId is reachable. Used to forbid link choices that would close a loop: if a
 * candidate already depends on the current item, linking the current item to it makes
 * a cycle.
 */
export const collectDependents = (graph, targetId) => {
  const target = String(targetId);
  const reverse = new Map();
  graph.forEach((sources, x) => {
    sources.forEach((s) => {
      if (!reverse.has(s)) reverse.set(s, new Set());
      reverse.get(s).add(x);
    });
  });

  const dependents = new Set();
  const stack = [target];
  while (stack.length) {
    const cur = stack.pop();
    const callers = reverse.get(cur);
    if (!callers) continue;
    callers.forEach((x) => {
      if (!dependents.has(x)) {
        dependents.add(x);
        stack.push(x);
      }
    });
  }
  return dependents;
};

/** Ids that participate in a circular dependency (defensive — should be empty in practice). */
export const findCyclicIds = (graph) => {
  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = new Map();
  const inCycle = new Set();
  graph.forEach((_, node) => color.set(node, WHITE));

  const path = [];
  const visit = (node) => {
    color.set(node, GRAY);
    path.push(node);
    (graph.get(node) || new Set()).forEach((next) => {
      if (!graph.has(next)) return; // edge to an item outside this set — ignore
      const c = color.get(next);
      if (c === GRAY) {
        const idx = path.lastIndexOf(next);
        for (let i = idx; i < path.length; i++) inCycle.add(path[i]);
      } else if (c === WHITE) {
        visit(next);
      }
    });
    path.pop();
    color.set(node, BLACK);
  };

  graph.forEach((_, node) => {
    if (color.get(node) === WHITE) visit(node);
  });
  return inCycle;
};

/**
 * Resolve links across a whole set of sibling line items, propagating through chains
 * (A → B → C). For each item that has linked rows, re-read the source quantities,
 * recompute the item's rows and quantity, and write both back via the supplied callbacks.
 *
 * Runs a bounded loop so chains settle. Items that participate in a circular link are
 * skipped (left at their stored value) so resolution stays deterministic.
 *
 * @param {Array} items                Sibling line items (the same set offered for linking).
 * @param {object} opts
 * @param {(item:any)=>Array} opts.rowsOf        Returns the calculator rows for an item.
 * @param {(item:any, rows:Array)=>void} opts.setRows      Persists resolved rows for an item.
 * @param {(item:any, qty:number)=>void} opts.setQuantity  Persists the recomputed quantity.
 * @param {number} [opts.maxPasses=5]
 */
export const resolveLineItemLinks = (items, opts) => {
  const { rowsOf, setRows, setQuantity, maxPasses = 5 } = opts || {};
  if (!Array.isArray(items) || items.length === 0) return;

  // Snapshot each item's rows once, and skip any item caught in a circular link.
  const rowsById = {};
  items.forEach((item) => {
    const id = linkKey(item);
    if (id != null) rowsById[id] = rowsOf(item) || [];
  });
  const cyclicIds = findCyclicIds(buildLinkGraph(items, rowsById));

  const qtyMap = buildSourceQtyMap(items);

  for (let pass = 0; pass < maxPasses; pass++) {
    let changedAny = false;

    items.forEach((item) => {
      const id = linkKey(item);
      if (id != null && cyclicIds.has(id)) return; // defensive: leave cyclic items alone

      const rows = id != null && rowsById[id] !== undefined ? rowsById[id] : rowsOf(item);
      if (!rows || rows.length === 0) return;
      if (!rows.some((r) => r && r.linkedSourceId != null)) return;

      const { rows: resolved, changed } = resolveRows(rows, qtyMap, id);
      if (!changed) return;

      const newQty = sumRowTotals(resolved);
      if (id != null) rowsById[id] = resolved;
      setRows(item, resolved);
      if (id != null) qtyMap.set(id, newQty);
      if (typeof setQuantity === 'function') setQuantity(item, newQty);
      changedAny = true;
    });

    if (!changedAny) break;
  }
};
